import clsx from 'clsx';
import { Button } from '@/components/ui/Button';
import { ACTION_LABEL, useErrorAction } from '@/hooks/useErrorAction';
import { useUiStore, type Toast, type ToastKind } from '@/store/ui';

const KIND_CLASS: Record<ToastKind, string> = {
  info: 'bg-panel-2 border-line text-text',
  success: 'bg-ok/15 border-ok/40 text-ok',
  warn: 'bg-warn/15 border-warn/40 text-warn',
  error: 'bg-err/15 border-err/40 text-err',
};

/**
 * 单条 Toast。
 *
 * 独立成子组件（而非在 `map` 内联）是因为「动作按钮」需要 `useErrorAction()` 这一 hook ——
 * hook 不能条件式、也不能在循环回调里调用。
 * 仅当 `action !== 'none'` **且**能解析出 handler（内置路由或注入的 `onAction`）时才渲染按钮，
 * 否则**不渲染**（诚实降级）。
 */
function ToastItem({ toast }: { toast: Toast }): JSX.Element {
  const dismiss = useUiStore((s) => s.dismissToast);
  const resolve = useErrorAction();

  const action = toast.action;
  const handler = action && action !== 'none' ? resolve(action, toast.onAction) : null;
  const label = action ? ACTION_LABEL[action] : '';

  return (
    <div
      role="status"
      className={clsx('pointer-events-auto rounded-btn border px-3 py-2 text-sm shadow-lg animate-slide-up', KIND_CLASS[toast.kind])}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="break-words">{toast.text}</span>
        <button
          type="button"
          aria-label="关闭提示"
          className="shrink-0 text-dim hover:text-text"
          onClick={() => dismiss(toast.id)}
        >
          ✕
        </button>
      </div>
      {handler ? (
        <div className="mt-2 flex justify-end">
          <Button variant="ghost" size="sm" onClick={handler}>
            {label}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** Toast 容器 —— 挂一次即可（见 `App.tsx`）。 */
export function ToastHost(): JSX.Element | null {
  const toasts = useUiStore((s) => s.toasts);

  if (toasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed bottom-6 left-1/2 z-[60] flex w-[min(92vw,420px)] -translate-x-1/2 flex-col gap-2">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  );
}

export default ToastHost;
